import React from 'react';
import { HiCheckCircle, HiXCircle } from 'react-icons/hi';

import { Modal } from '../../../../shared/ui/Modal';
import { SecondaryButton } from '../../../../shared/ui/Inputs';
import { Spinner } from '../../../../shared/ui/Spinners';

type ExportOrdersProgressModalProps = {
    open: boolean;
    totalOrders: number;
    processedOrders: number;
    stage: string;
    errorCount?: number;
    canClose?: boolean;
    limitMessage?: string;
    onCancel?: () => void;
    onClose?: () => void;
};

export const ExportOrdersProgressModal: React.FC<
    ExportOrdersProgressModalProps
> = ({
    open,
    totalOrders,
    stage,
    errorCount = 0,
    canClose = false,
    limitMessage,
    onCancel,
    onClose,
}) => {
        const isLargeExport = totalOrders > 50;

        // `stage` vem do fluxo de exportação
        // `canClose` é habilitado quando o fluxo terminou (concluído/cancelado/erro).
        const isDone = canClose && stage.toLowerCase().includes('conclu');
        const isCancelled = canClose && stage.toLowerCase().includes('cancel');

        const subtitle = (() => {
            if (isDone) return 'Planilha gerada. Você já pode fechar.';
            if (isCancelled) return 'A exportação foi cancelada.';
            if (isLargeExport) {
                return `Foram encontrados ${totalOrders} pedidos. Essa exportação pode demorar.`;
            }
            return 'Preparando a planilha com detalhes dos produtos.';
        })();

        return (
            <Modal
                open={open}
                handleClose={canClose ? onClose : undefined}
                width="520px"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <h2 style={{ margin: 0, textAlign: 'center' }}>Exportando pedidos</h2>
                        {limitMessage ? (
                            <div
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    background: '#e8f4fd',
                                    color: '#0b5394',
                                    fontWeight: 800,
                                    lineHeight: 1.35,
                                    textAlign: 'center',
                                }}
                            >
                                {limitMessage}
                            </div>
                        ) : null}
                        {isLargeExport && !isDone && !isCancelled ? (
                            <div
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    background: '#fff3cd',
                                    color: '#856404',
                                    fontWeight: 700,
                                    lineHeight: 1.35,
                                }}
                            >
                                {subtitle} Pode ocorrer instabilidade durante o processo.
                            </div>
                        ) : (
                            <div style={{ color: '#444', textAlign: 'center' }}>{subtitle}</div>
                        )}
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px',
                            minHeight: '86px',
                        }}
                    >
                        {!canClose ? (
                            <Spinner />
                        ) : isDone ? (
                            <HiCheckCircle style={{ fontSize: '32px', color: '#2e7d32' }} />
                        ) : isCancelled ? (
                            <HiXCircle style={{ fontSize: '32px', color: '#c62828' }} />
                        ) : (
                            <HiCheckCircle style={{ fontSize: '32px', color: '#2e7d32' }} />
                        )}

                        <div style={{ fontWeight: 800, textAlign: 'center' }}>{stage}</div>
                        {errorCount > 0 ? (
                            <div style={{ color: '#666', textAlign: 'center' }}>
                                {errorCount} falhas
                            </div>
                        ) : null}
                    </div>

                    <div
                        style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}
                    >
                        {typeof onCancel === 'function' && !canClose ? (
                            <SecondaryButton onClick={onCancel}>Cancelar</SecondaryButton>
                        ) : null}
                        {canClose ? (
                            <SecondaryButton onClick={onClose}>Fechar</SecondaryButton>
                        ) : null}
                    </div>
                </div>
            </Modal>
        );
    };
